import { useMemo } from 'react';
import Head from 'next/head';
import { cloudflareLoader } from '~/shared/components/Image/loaders/cloudflareLoader';
import { useConfig } from '~/shared/hooks';
import { SFProduct } from '~/features/product-details-page/pdp.definitions';
import { extractMetaImage } from '~/features/product-details-page/pdp-utils';

interface PDPMetafieldsProps {
    product: SFProduct;
}

/**
 * INFO: This acts as an override to the MetaFields component on the Standard
 * layout for PDPs (product detail pages). These page metadata will be used on mount.
 */
export const PDPMetaFields: React.FC<PDPMetafieldsProps> = ({ product }) => {
    const { config } = useConfig();

    const { c_pageURL, c_canonical, id, pageTitle } = product;

    const canonicalUrl = new URL(
        c_pageURL || c_canonical || '/',
        config.NEXT_PUBLIC_URL,
    ).toString();
    const ogUrl = new URL(c_pageURL || '/', config.NEXT_PUBLIC_URL).toString();

    const imageUrl = useMemo(() => {
        const metaImage = extractMetaImage(product);

        if (!metaImage?.images[0]?.link) return null;

        return cloudflareLoader({ src: metaImage.images[0].link, width: 1200 });
    }, [id]);

    return (
        <Head>
            {pageTitle && (
                <>
                    <title key="title">{pageTitle}</title>

                    <meta key="metaName" name="name" content={pageTitle} />
                    <meta key="ogTitle" name="og:title" content={pageTitle} />
                </>
            )}

            {canonicalUrl && <link key="canonical" rel="canonical" href={canonicalUrl} />}

            {imageUrl && <meta key="ogImage" name="og:image" content={imageUrl} />}
            {ogUrl && <meta key="ogUrl" name="og:url" content={ogUrl} />}
        </Head>
    );
};
